import {
  IMeasureDisplayFormatFieldsFragment,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import {
  defaultNumberFormatOptions,
  formatValueWithMeasureFormat,
  globalFormat,
} from '../../../utils/formatting';
import { assertNotNil } from '../../../utils/types';
import {
  ITooltipContext,
  TooltipContextType,
} from '../CustomTooltip/tooltipContextInterfaces';
import {
  FUNNEL_FREQUENCY_X_AXIS_KEY,
  FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY,
  ILineMetaNewExpression,
  ILineMetaType,
  IParsedChartData,
  IParsedChartDataRecord,
} from '../interfaces';
import { ITooltipProps } from '../hooks/tooltip/useSingleChartDataTooltip';
import * as SingleChartDataTooltipUI from '../SingleChartDataTooltip/SingleChartDataTooltipUI';
import {
  funnelAbsoluteValue,
  funnelRelativeValue,
} from '../CustomTooltip/utils';
import { isNil } from 'lodash';

interface IProps {
  tooltipProps: ITooltipProps;
  measureFormat: IMeasureDisplayFormatFieldsFragment | null;
  funnelTooltipContext?: ITooltipContext;
  chartData: IParsedChartData;
  dataWithOthers?: IParsedChartDataRecord[];
  linesMetaWithOthers?: ILineMetaNewExpression;
  singleDayLabel: string;
  isFunnelRelativeLabels: boolean;
}

/**
 * This component is used to display the value of the line in the tooltip.
 * It will display the value based on the chart type and the data.
 */
const TooltipLineValue = ({
  tooltipProps,
  funnelTooltipContext,
  measureFormat,
  chartData,
  dataWithOthers,
  singleDayLabel,
  linesMetaWithOthers,
  isFunnelRelativeLabels,
}: IProps) => {
  const renderValue = () => {
    if (chartData.queryType === QueryType.Expression) {
      const xAxisKey = tooltipProps.xAxisKey;
      // If others exists, it's in AREA/StackedColumn/Pie chart.
      if (dataWithOthers && linesMetaWithOthers) {
        const dayData = dataWithOthers?.find((d) => {
          return d[xAxisKey] === tooltipProps.date;
        });
        assertNotNil(dayData);
        const value = dayData[tooltipProps.line.name];
        assertNotNil(value);
        // hint lineMeta is the opposite of the lineMeta,
        // for example, if lineMeta is percent, hintLineMeta is the base. Vice versa.
        const lineMeta = linesMetaWithOthers[tooltipProps.line.name];
        assertNotNil(lineMeta);
        let hintLineMeta: ILineMetaNewExpression[keyof ILineMetaNewExpression];
        if (lineMeta.type === ILineMetaType.Percent) {
          assertNotNil(lineMeta.parentKey);
          hintLineMeta = linesMetaWithOthers[lineMeta.parentKey];
        } else {
          assertNotNil(lineMeta.percentLineKey);
          hintLineMeta = linesMetaWithOthers[lineMeta.percentLineKey];
        }
        assertNotNil(hintLineMeta);
        const baseValueDisplay =
          lineMeta.type === ILineMetaType.Percent
            ? globalFormat.formatNumber(
                Number(value),
                defaultNumberFormatOptions.percent,
              )
            : formatValueWithMeasureFormat(Number(value), measureFormat);
        const hintValue = dayData[hintLineMeta.name];
        if (isNil(hintValue) || isNaN(Number(hintValue))) {
          return baseValueDisplay;
        }

        const hintValueDisplay =
          hintLineMeta.type === ILineMetaType.Percent
            ? globalFormat.formatNumber(
                Number(hintValue),
                defaultNumberFormatOptions.percent,
              )
            : formatValueWithMeasureFormat(Number(hintValue), measureFormat);
        return `${baseValueDisplay} (${hintValueDisplay})`;
      } else {
        // for regular bar and line charts.
        let dayData;
        if (!singleDayLabel) {
          dayData = chartData.data.find((d) => {
            return d[xAxisKey] === tooltipProps.date;
          });
        } else {
          dayData = chartData.data.find((d) => {
            return d.date === singleDayLabel;
          });
        }
        assertNotNil(dayData);
        const value = dayData[tooltipProps.line.name];
        assertNotNil(value);
        return formatValueWithMeasureFormat(Number(value), measureFormat);
      }
    } else if (chartData.queryType === QueryType.Funnel) {
      assertNotNil(funnelTooltipContext);
      const lineValues = chartData.data.find((data) => {
        if (
          funnelTooltipContext?.type === TooltipContextType.FUNNEL_FREQUENCY
        ) {
          return tooltipProps.date === data[FUNNEL_FREQUENCY_X_AXIS_KEY];
        } else if (
          funnelTooltipContext?.type ===
          TooltipContextType.FUNNEL_TIME_TO_CONVERT
        ) {
          return tooltipProps.date === data[FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY];
        }
        return tooltipProps.date === data.date;
      });
      assertNotNil(lineValues);
      if (
        funnelTooltipContext?.type ===
          TooltipContextType.FUNNEL_TIME_TO_CONVERT ||
        funnelTooltipContext?.type === TooltipContextType.FUNNEL_FREQUENCY
      ) {
        const dataKeys =
          funnelTooltipContext.dataKeysPerLine[tooltipProps.line.name];
        return <span>{funnelAbsoluteValue(lineValues, dataKeys, false)}</span>;
      } else if (
        funnelTooltipContext.type === TooltipContextType.FUNNEL_CONVERSION ||
        funnelTooltipContext.type ===
          TooltipContextType.FUNNEL_CONVERSION_SINGLE
      ) {
        const dataKeys =
          funnelTooltipContext.dataKeysPerLine[tooltipProps.line.name];

        return isFunnelRelativeLabels
          ? funnelRelativeValue(lineValues, dataKeys)
          : funnelAbsoluteValue(lineValues, dataKeys, true);
      }
    }
  };
  return (
    <SingleChartDataTooltipUI.ValueLabel>
      {renderValue()}
    </SingleChartDataTooltipUI.ValueLabel>
  );
};

export default TooltipLineValue;
